import React from 'react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import { Camera, MapPin, Heart, Shield, CreditCard, Users } from 'lucide-react';

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-white pt-16 flex flex-col">
      <SEO 
        title="How GearLoop Works | Rent & Share Outdoor Equipment"
        description="Discover how to rent outdoor gear from locals or earn by sharing your equipment. Simple, secure, and sustainable outdoor gear rental process explained."
        keywords={[
          'how to rent outdoor gear',
          'share outdoor equipment',
          'gear rental process',
          'outdoor equipment rental guide',
          'rent camping gear guide',
          'local gear sharing',
          'outdoor gear marketplace guide',
          'peer to peer rental process'
        ]}
        type="article"
      />

      <main className="flex-grow">
        {/* Hero Section */}
        <AnimatedSection>
          <div className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h1 className="text-4xl font-bold text-center mb-6">How GearLoop Works</h1>
              <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto">
                Whether you're looking to rent out your gear or find equipment for your next adventure,
                GearLoop makes it simple and secure.
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* For Gear Owners */}
        <AnimatedSection delay={0.2}>
          <div className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-16">Listing Your Gear</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Camera className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Create Your Listing</h3>
                  <p className="text-gray-600">
                    Take clear photos of your gear, write a detailed description, and set your rental price and availability.
                    Include any specific requirements or rules for renters.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Verify Renters</h3>
                  <p className="text-gray-600">
                    Review rental requests and renter profiles. Our verification system helps ensure your 
                    gear is in good hands. Communicate through our secure messaging system.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CreditCard className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Get Paid</h3>
                  <p className="text-gray-600">
                    Receive secure payments directly to your account. Our protection policies cover 
                    damage or loss, giving you peace of mind with every rental.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* For Renters */}
        <AnimatedSection delay={0.3}>
          <div className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-16">Finding and Renting Gear</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Search Nearby</h3>
                  <p className="text-gray-600">
                    Browse available gear in your area. Filter by category, dates needed, and price range. 
                    View detailed photos and descriptions of each item.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Book & Connect</h3>
                  <p className="text-gray-600">
                    Request to rent gear through our secure platform. Communicate with owners to arrange 
                    pickup or delivery details. Complete your identity verification.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Adventure Time</h3>
                  <p className="text-gray-600">
                    Pick up your gear and head out on your adventure. Return the equipment in the same 
                    condition, and share your experience through reviews.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Trust & Safety */}
        <AnimatedSection delay={0.4}>
          <div className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold mb-8">Trust & Safety</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                GearLoop provides comprehensive protection for both owners and renters. Our secure platform includes:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Secure Payments</h3>
                  <p className="text-gray-600">Protected transactions and damage deposits</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">ID Verification</h3>
                  <p className="text-gray-600">Multi-step verification process for all users</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Insurance Coverage</h3>
                  <p className="text-gray-600">Protection against damage or loss</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">24/7 Support</h3>
                  <p className="text-gray-600">Dedicated customer service team</p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>

      <Footer />
    </div>
  );
}